// components/InvoiceRequest/InvoiceRequestEditDialog.tsx
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormHelperText,
    Alert,
    CircularProgress,
    Box,
    FormControlLabel,
    Switch,
    Typography,
    Grid,
    InputAdornment
} from '@mui/material';
import { invoiceRequestService } from '../../services/invoiceRequest';
import { InvoiceRequest } from '../../types/invoiceRequest';
import { useUser } from '../../hooks/useUser';
import { NumberTextField } from '../Common/NumberTextField';

interface InvoiceRequestEditDialogProps {
    open: boolean;
    onClose: () => void;
    invoiceRequest: InvoiceRequest | null; // IDではなく直接オブジェクトを受け取る
    onSuccess: () => void;
}

export const InvoiceRequestEditDialog: React.FC<InvoiceRequestEditDialogProps> = ({
    open,
    onClose,
    invoiceRequest,
    onSuccess
}) => {
    const { userInfo } = useUser();
    const [billingMonth, setBillingMonth] = useState<string>('');
    const [remarks, setRemarks] = useState('');
    const [isCompleted, setIsCompleted] = useState(false);
    const [invoiceAmount, setInvoiceAmount] = useState<string>(''); // 請求金額
    const [useEstimatedAmount, setUseEstimatedAmount] = useState(false); // 見積金額を使用するフラグ
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [updating, setUpdating] = useState(false);

    // データ取得
    useEffect(() => {
        if (invoiceRequest) {
            // 日付をYYYY-MM形式に変換
            const date = new Date(invoiceRequest.Billing_Month);
            const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

            setBillingMonth(yearMonth);
            setRemarks(invoiceRequest.Remarks || '');

            // Is_Completedを確実にブール型に変換
            setIsCompleted(!!invoiceRequest.Is_Completed);

            // 請求金額の設定
            if (invoiceRequest.Invoice_Amount) {
                setInvoiceAmount(invoiceRequest.Invoice_Amount.toString());
                setUseEstimatedAmount(false);
            } else if (invoiceRequest.Estimated_Amount) {
                setInvoiceAmount(invoiceRequest.Estimated_Amount.toString());
                setUseEstimatedAmount(true);
            } else {
                setInvoiceAmount('0');
                setUseEstimatedAmount(false);
            }
        }
    }, [invoiceRequest, open]);


    const handleSubmit = async () => {
        if (!billingMonth) {
            setError('請求月を選択してください');
            return;
        }

        setUpdating(true);
        setError('');

        try {
            // YYYY-MM-01 形式に変換
            const formattedDate = `${billingMonth}-01`;

            // ログインユーザーの苗字を取得
            const updatedByName = userInfo?.lastName || "更新者";

            // 請求金額の処理
            let finalInvoiceAmount: number;
            if (useEstimatedAmount && invoiceRequest?.Estimated_Amount) {
                finalInvoiceAmount = invoiceRequest.Estimated_Amount;
            } else {
                finalInvoiceAmount = invoiceAmount ? parseInt(invoiceAmount) : 0;
            }

            // デバッグ用ログ
            console.log('更新する請求金額:', finalInvoiceAmount);

            await invoiceRequestService.update(invoiceRequest?.ID!, {
                billingMonth: formattedDate,
                remarks,
                isCompleted,
                updatedByName,
                invoiceAmount: finalInvoiceAmount
            });

            onSuccess();
            handleClose();
        } catch (err: any) {
            console.error('請求書作成依頼の更新に失敗しました:', err);
            setError('請求書作成依頼の更新に失敗しました');
        } finally {
            setUpdating(false);
        }
    };



    // 見積金額を使用する設定の切り替え
    const handleUseEstimatedAmount = (checked: boolean) => {
        setUseEstimatedAmount(checked);
        if (checked && invoiceRequest?.Estimated_Amount) {
            setInvoiceAmount(invoiceRequest.Estimated_Amount.toString());
        }
    };

    // 入力フィールドは数値のみ許可
    const handleInvoiceAmountChange = (value: string) => {
        // 数値のみを許可（カンマなし）
        const numericValue = value.replace(/[^0-9]/g, '');
        setInvoiceAmount(numericValue);
    };

    // handleCloseも修正して状態をリセット
    const handleClose = () => {
        setBillingMonth('');
        setRemarks('');
        setIsCompleted(false);
        setInvoiceAmount('');
        setUseEstimatedAmount(false);
        setError('');
        onClose();
    };

    // 日付のフォーマット
    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return `${date.getFullYear()}年${(date.getMonth() + 1).toString().padStart(2, '0')}月${date.getDate().toString().padStart(2, '0')}日`;
    };

    if (loading) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableRestoreFocus>
            <DialogTitle sx={{
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'grey.50',
                px: 3,
                py: 2
            }}>
                請求書作成依頼の編集
            </DialogTitle>
            <DialogContent sx={{ p: 3, pt: 3 }}>
                {invoiceRequest ? (
                    <>
                        <Grid container spacing={2}>
                            {/* 既存の編集フィールド */}
                            <Grid item xs={12}>
                                <TextField
                                    label="請求月"
                                    type="month"
                                    value={billingMonth}
                                    onChange={(e) => setBillingMonth(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    error={!!error && !billingMonth}
                                    helperText={!!error && !billingMonth ? '請求月を選択してください' : ''}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            {/* 見積金額（表示のみ） */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="見積金額"
                                    value={invoiceRequest.Estimated_Amount ?
                                        `¥${invoiceRequest.Estimated_Amount.toLocaleString()}` :
                                        '-'}
                                    fullWidth
                                    variant="outlined"
                                    InputProps={{ readOnly: true }}
                                />
                            </Grid>

                            {/* 請求金額 */}
                            <Grid item xs={12} sm={6}>
                                <NumberTextField
                                    label="請求金額"
                                    value={invoiceAmount}
                                    onChange={(e) => handleInvoiceAmountChange(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    disabled={useEstimatedAmount}
                                    thousandSeparator={true}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">¥</InputAdornment>
                                    }}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={useEstimatedAmount}
                                            onChange={(e) => handleUseEstimatedAmount(e.target.checked)}
                                        />
                                    }
                                    label="見積金額と同じ"
                                    sx={{ mt: 1 }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="申し送り事項"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isCompleted}
                                            onChange={(e) => setIsCompleted(e.target.checked)}
                                        />
                                    }
                                    label="請求書作成済み"
                                />
                            </Grid>
                        </Grid>
                    </>
                ) : (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography>データの読み込みに失敗しました</Typography>
                    </Box>
                )}

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{
                borderTop: 1,
                borderColor: 'divider',
                px: 3,
                py: 2
            }}>
                <Button
                    onClick={handleClose}
                    color="inherit"
                    disabled={updating}
                    variant="outlined"
                >
                    キャンセル
                </Button>
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                    disabled={updating || !invoiceRequest}
                    startIcon={updating ? <CircularProgress size={20} color="inherit" /> : undefined}
                >
                    {updating ? '更新中...' : '更新する'}
                </Button>
            </DialogActions>
        </Dialog>
    );

};